import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import { useAppDispatch } from "app/hooks";
import Dialog from "components/dialog";
import PatientSalesSelectList from "features/patients/sections/patient-billing/components/patient-sales-select-list";
import { useLazyGetMergeTagsSaleByIdQuery } from "services/merge-tags";
import { type Sale } from "services/types/sales.type";

import { selectDataParamsById, setDataParams } from "../word-processor.slice";

type SelectSaleDialogValues = {
  sale: Sale | null;
};

interface WordProcessorSelectSaleDialogProps {
  id: string;
}

export default function WordProcessorSelectSaleDialog({
  id
}: WordProcessorSelectSaleDialogProps) {
  const { zIndex } = useTheme();
  const dispatch = useAppDispatch();
  const dataParams =
    useSelector((state: any) => selectDataParamsById(state, id)) || {};

  const [isBusy, setIsBusy] = useState(false);

  const [prefetchSale] = useLazyGetMergeTagsSaleByIdQuery();

  const defaultValues: SelectSaleDialogValues = useMemo(() => {
    return merge({}, { sale: null });
  }, []);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid }
  } = useForm<SelectSaleDialogValues>({
    mode: "onBlur",
    defaultValues
  });

  const handleClose = useCallback(() => {
    reset();
    setIsBusy(false);
    dispatch(setDataParams({ id, openSelectSaleDialog: false }));
  }, [dispatch, id, reset]);

  const handleSave = useCallback(
    async (data: SelectSaleDialogValues) => {
      setIsBusy(true);
      dispatch(setDataParams({ id, saleId: data.sale?.id }));
      await prefetchSale({ id: Number(data.sale?.id) });
      handleClose();
    },
    [dispatch, handleClose, id, prefetchSale]
  );

  return (
    <Dialog
      title="Please, select invoice"
      onClose={handleClose}
      open={Boolean(
        id && dataParams.openSelectSaleDialog && dataParams.patientId
      )}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column"
        }
      }}
      sx={{ zIndex: zIndex.snackbar + 3 }}
    >
      <Box height={320} maxHeight={320} width="100%" mb={1}>
        <PatientSalesSelectList
          control={control}
          patientId={dataParams.patientId}
          title=""
          showSelected
        />
      </Box>
      <DialogActions>
        <Button variant="outlined" size="small" onClick={handleClose}>
          Cancel
        </Button>

        <LoadingButton
          loading={isBusy}
          disabled={!isValid}
          variant="contained"
          size="small"
          onClick={handleSubmit(handleSave)}
        >
          Select
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
